import { Injectable, inject } from '@angular/core';
import { isEqual } from 'lodash';
import type { TableCardConfig } from '../models/table-config.model';
import {
	ConfigChange,
	ChangeResult,
	ValidationWarning,
} from './card-config-change.types';
import { sanitizeCardDataConfigForGql } from '../utils/card-data-gql.util';
import {
	columnIdsFromTableData,
	pruneVisualizationColumnsToConfiguredIds,
} from '../utils/table-column.util';
import {mergeTableFeaturesIntoVisualization, isTableTabResetVisualizationPatch, visualizationRecord} from '../utils/table-presentation-visualization.util';
import {TableConfigValidator} from './table-config-validator.service';
import {CardPersistenceService} from './card-persistence.service';

interface PatchChanges {
	dataChanged: boolean;
	visualizationChanged: boolean;
	featuresChanged: boolean;
	otherChanged: boolean;
}

@Injectable({
	providedIn: 'root',
})
export class TableConfigChangeService {
	private readonly validator = inject(TableConfigValidator);
	private readonly persistence = inject(CardPersistenceService);

	previewChange(
		current: TableCardConfig,
		change: ConfigChange
	): ValidationWarning[] {
		const prospective = this.buildProspectiveConfig(current, change);
		return this.validator.validate(prospective);
	}

	applyChange(
		cardId: string,
		current: TableCardConfig,
		change: ConfigChange
	): ChangeResult<TableCardConfig> {
		const patch = change.patch as Partial<TableCardConfig>;
		let prospective = this.buildProspectiveConfig(current, change);

		const warnings: ValidationWarning[] = [
			...this.validator.validate(prospective),
		];

		const patchForPersistence = this.buildPatchForPersistence(
			change,
			prospective
		);
		if (Object.keys(patchForPersistence).length > 0) {
			this.persistence.queueChange(cardId, patchForPersistence);
		}

		const trackedRevisions = this.persistence.getCurrentRevisions(cardId);
		if (trackedRevisions !== null) {
			prospective = { ...prospective, revisions: trackedRevisions };
		}

		return {
			config: prospective,
			warnings,
			migrated: false,
		};
	}

	private buildProspectiveConfig(
		current: TableCardConfig,
		change: ConfigChange
	): TableCardConfig {
		const patch = change.patch as Partial<TableCardConfig>;
		const changes = this.detectChanges(current, patch);

		let prospective: TableCardConfig = {
			...current,
			...patch,
		};

		if (changes.dataChanged && patch.data) {
			const mergedData = this.deepMergeWithNullDeletes(
				(current.data ?? {}) as Record<string, unknown>,
				patch.data as Record<string, unknown>
			) as TableCardConfig['data'];
			prospective = {
				...prospective,
				data: this.#clearDateRangeWhenUsingGlobalDefault(mergedData),
			};
		}

		if (changes.featuresChanged && patch.features) {
			const mergedFeatures = this.deepMerge(
				(current.features ?? {}) as Record<string, unknown>,
				patch.features as Record<string, unknown>
			) as TableCardConfig['features'];
			prospective = {
				...prospective,
				features: mergedFeatures,
				visualization: mergeTableFeaturesIntoVisualization(
					prospective.visualization ?? current.visualization,
					mergedFeatures
				) as TableCardConfig['visualization'],
			};
		}

		if (changes.visualizationChanged && patch.visualization !== undefined) {
			const useAiNullDeletes = change.origin === 'ai';
			const incomingRecord = patch.visualization as Record<string, unknown>;
			const useNullDeletes =
				useAiNullDeletes ||
				(change.origin === 'form' &&
					isTableTabResetVisualizationPatch(incomingRecord));
			prospective = {
				...prospective,
				visualization: this.mergeVisualization(
					current.visualization,
					patch.visualization,
					useNullDeletes
				),
			};
		}

		if (patch.style) {
			prospective = {
				...prospective,
				style: this.deepMerge(
					(current.style ?? {}) as Record<string, unknown>,
					(patch.style ?? {}) as Record<string, unknown>
				) as TableCardConfig['style'],
			};
		}

		return this.#pruneStaleVisualizationColumns(prospective);
	}

	/**
	 * Cards on the report/page default must not keep a chart-specific
	 * `dateRange` left behind by deep-merge when the patch only flips
	 * `timeDimensionSource` to `global` (or omits `dateRange` after GQL sanitize).
	 */
	#clearDateRangeWhenUsingGlobalDefault(
		data: TableCardConfig['data']
	): TableCardConfig['data'] {
		const td = data?.timeDimension;
		if (!td) {
			return data;
		}
		const source = String(td.timeDimensionSource ?? '')
			.trim()
			.toLowerCase();
		if (source !== 'global' || td.dateRange === undefined) {
			return data;
		}
		const { dateRange: _removed, ...rest } = td;
		return {
			...data,
			timeDimension: rest,
		};
	}

	#pruneStaleVisualizationColumns(
		config: TableCardConfig
	): TableCardConfig {
		if (config.visualization === undefined) {
			return config;
		}
		const viz = visualizationRecord(config.visualization);
		const pruned = pruneVisualizationColumnsToConfiguredIds(
			viz,
			columnIdsFromTableData(config)
		);
		if (pruned === viz) {
			return config;
		}
		return {
			...config,
			visualization: pruned as TableCardConfig['visualization'],
		};
	}

	/**
	 * Debounced autosave: query fields on `data`, presentation on `visualization` (AWSJSON).
	 * Skips `runtime` origin (paging offsets) and strips non-AppSync `data` keys.
	 */
	private buildPatchForPersistence(
		change: ConfigChange,
		prospective: TableCardConfig
	): Partial<TableCardConfig> {
		if (change.origin === 'runtime') {
			return {};
		}

		const patch: Partial<TableCardConfig> = {};
		const incoming = change.patch as Partial<TableCardConfig>;

		if (incoming.data !== undefined && prospective.data !== undefined) {
			const sanitized = sanitizeCardDataConfigForGql(
				prospective.data as Record<string, unknown>
			);
			if (Object.keys(sanitized).length > 0) {
				patch.data = sanitized as TableCardConfig['data'];
			}
		}

		if (incoming.visualization !== undefined || incoming.features !== undefined) {
			patch.visualization = prospective.visualization;
		}

		const scalarKeys = [
			'title',
			'subtitle',
			'content',
			'icon',
			'image',
			'clientId',
			'pageId',
			'style',
		] as const;
		for (const key of scalarKeys) {
			if (incoming[key] !== undefined) {
				(patch as Record<string, unknown>)[key] = incoming[key];
			}
		}

		if (incoming.filterIds !== undefined) {
			patch.filterIds = incoming.filterIds;
		}

		return patch;
	}

	private detectChanges(
		current: TableCardConfig,
		patch: Partial<TableCardConfig>
	): PatchChanges {
		return {
			dataChanged:
				patch.data !== undefined && !isEqual(patch.data, current.data),
			visualizationChanged: patch.visualization !== undefined,
			featuresChanged:
				patch.features !== undefined &&
				!isEqual(patch.features, current.features),
			otherChanged: Object.keys(patch).some(
				key =>
					!['data', 'visualization', 'features', 'style'].includes(
						key
					) &&
					!isEqual(
						(patch as Record<string, unknown>)[key],
						(current as unknown as Record<string, unknown>)[key]
					)
			),
		};
	}

	private mergeVisualization(
		current: TableCardConfig['visualization'],
		incoming: TableCardConfig['visualization'],
		useNullDeletes: boolean
	): TableCardConfig['visualization'] {
		if (incoming === undefined) {
			return current;
		}
		if (typeof incoming === 'string') {
			return incoming;
		}
		if (!current || typeof current === 'string') {
			if (useNullDeletes) {
				return this.deepMergeWithNullDeletes(
					{},
					incoming as Record<string, unknown>
				) as TableCardConfig['visualization'];
			}
			return incoming;
		}

		const incomingRecord = incoming as Record<string, unknown>;
		if (Object.keys(incomingRecord).length === 0) {
			return {} as TableCardConfig['visualization'];
		}

		if (useNullDeletes) {
			return this.deepMergeWithNullDeletes(
				{ ...(current as Record<string, unknown>) },
				incomingRecord
			) as TableCardConfig['visualization'];
		}

		return this.deepMerge(
			current as Record<string, unknown>,
			incomingRecord
		) as TableCardConfig['visualization'];
	}

	private deepMergeWithNullDeletes(
		base: Record<string, unknown>,
		incoming: Record<string, unknown>
	): Record<string, unknown> {
		const result: Record<string, unknown> = { ...base };

		for (const key of Object.keys(incoming)) {
			const incomingValue = incoming[key];
			if (incomingValue === null) {
				delete result[key];
				continue;
			}
			// `data.order` is a single-field Cube map — replace atomically so a new
			// sort field does not accumulate stale keys from the previous order.
			if (key === 'order') {
				result[key] = incomingValue;
				continue;
			}
			const baseValue = result[key];
			if (
				baseValue !== null &&
				typeof baseValue === 'object' &&
				!Array.isArray(baseValue) &&
				typeof incomingValue === 'object' &&
				incomingValue !== null &&
				!Array.isArray(incomingValue)
			) {
				result[key] = this.deepMergeWithNullDeletes(
					baseValue as Record<string, unknown>,
					incomingValue as Record<string, unknown>
				);
			} else {
				result[key] = incomingValue;
			}
		}

		return result;
	}

	private deepMerge(
		base: Record<string, unknown>,
		incoming: Record<string, unknown>
	): Record<string, unknown> {
		const result: Record<string, unknown> = { ...base };

		for (const key of Object.keys(incoming)) {
			const baseValue = base[key];
			const incomingValue = incoming[key];

			if (
				baseValue !== null &&
				typeof baseValue === 'object' &&
				!Array.isArray(baseValue) &&
				incomingValue !== null &&
				typeof incomingValue === 'object' &&
				!Array.isArray(incomingValue)
			) {
				result[key] = this.deepMerge(
					baseValue as Record<string, unknown>,
					incomingValue as Record<string, unknown>
				);
			} else {
				result[key] = incomingValue;
			}
		}

		return result;
	}
}
